import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { vendorQueries } from '../../lib/queries';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { T, sans } from '../Vendors/VendorDetails';
import { VendorLogo } from '../VendorRequests/VendorRequests';

/* ─── Types ──────────────────────────────────────────────────────── */
interface VendorLite {
  id: string; name: string; domain: string;
  score: number | null; riskLevel: string | null;
  scoreHistory: number[]; previousScore: number | null; lastScan: string | null;
  category: string | null; location: string | null; lifecycle: string | null;
  dataSensitivity: string | null; ownerName: string | null; contractEnd: string | null;
}

interface Posture {
  has_scan: boolean;
  scan_date: string | null;
  counts: { critical: number; high: number; medium: number; low: number };
  category_scores: Record<string, number>;
  top_findings: { name: string; category: string | null; severity: string }[];
}

interface Col { vendor: VendorLite; posture: Posture | undefined; loading: boolean; color: string }

const MAX = 4;
const MIN = 2;
const COLORS = ['#2563EB', '#7C3AED', '#0D9488', '#DB2777'];
const GAP = 15;
const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
const LIFECYCLE: Record<string, string> = { due_diligence: 'Due diligence', active_monitoring: 'Active monitoring', offboarding: 'Offboarding' };
const SENSITIVITY: Record<string, string> = {
  none: 'No sensitive data', internal: 'Internal data only', pii: 'Customer PII',
  financial: 'Financial or payment data', regulated: 'Regulated data',
};

/* ─── Helpers ────────────────────────────────────────────────────── */
const scoreColor = (s: number) => (s >= 80 ? '#15803D' : s >= 60 ? '#B45309' : s >= 40 ? '#C2410C' : '#B91C1C');
const scoreColorDark = (s: number) => (s >= 80 ? '#4ADE80' : s >= 60 ? '#FACC15' : s >= 40 ? '#FB923C' : '#F87171');
const grade = (s: number) => (s >= 90 ? 'A' : s >= 80 ? 'B' : s >= 70 ? 'C' : s >= 60 ? 'D' : 'F');
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const daysSince = (iso: string | null) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
const agoText = (iso: string | null) => {
  const d = daysSince(iso);
  if (d === null) return 'Never';
  if (d <= 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 60) return `${d} days ago`;
  return fmtDate(iso);
};
const csvCell = (v: unknown) => {
  let s = v === null || v === undefined ? '' : String(v);
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/* ─── Theme ──────────────────────────────────────────────────────── */
const palette = (isDark: boolean) => ({
  isDark,
  ink: isDark ? '#e2e8f0' : T.ink,
  ink2: isDark ? '#cbd5e1' : T.ink2,
  slate: isDark ? '#94a3b8' : T.slate,
  muted: isDark ? '#7c8aa0' : T.muted,
  line: isDark ? '#1f1f1f' : T.bdr,
  line2: isDark ? '#171717' : '#EEF2F6',
  surface: isDark ? '#0d0d0d' : '#FFFFFF',
  surface2: isDark ? '#141414' : '#F8FAFC',
  hover: isDark ? '#121212' : '#F8FAFC',
  bg: isDark ? '#000000' : T.bg,
  accent: isDark ? '#3B82F6' : '#2563EB',
  accentBg: isDark ? '#0f1e38' : '#EFF6FF',
  red: isDark ? '#F87171' : '#B91C1C',
  orange: isDark ? '#FB923C' : '#C2410C',
  amber: isDark ? '#FACC15' : '#B45309',
  green: isDark ? '#4ADE80' : '#15803D',
  greenBg: isDark ? '#0b2214' : '#F0FDF4',
  sc: (s: number) => (isDark ? scoreColorDark(s) : scoreColor(s)),
});
type P = ReturnType<typeof palette>;

const btn = (c: P, kind: 'primary' | 'secondary' | 'ghost' = 'secondary', small = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: small ? '5px 10px' : '8px 14px', borderRadius: 8, fontSize: small ? 12 : 12.5, fontWeight: 600,
  fontFamily: sans, cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.2,
  border: kind === 'secondary' ? `1px solid ${c.line}` : '1px solid transparent',
  background: kind === 'primary' ? c.accent : kind === 'ghost' ? 'transparent' : c.surface,
  color: kind === 'primary' ? '#fff' : kind === 'ghost' ? c.slate : c.ink,
});
const panel = (c: P): React.CSSProperties => ({ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12 });
const eyebrow = (c: P): React.CSSProperties => ({ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: c.muted });

/* ─── Icons ──────────────────────────────────────────────────────── */
const I: Record<string, string> = {
  close: 'M6 6l12 12M18 6 6 18',
  plus: 'M12 5v14M5 12h14',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.3-4.3',
  external: 'M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14',
  link: 'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7',
  up: 'M12 19V5M5 12l7-7 7 7',
  down: 'M12 5v14M19 12l-7 7-7-7',
  chevron: 'M6 9l6 6 6-6',
  check: 'M5 12l5 5L20 7',
  trophy: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4ZM17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3',
  warn: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  scale: 'M12 3v18M5 7h14M5 7l-3 7a4 4 0 0 0 6 0L5 7ZM19 7l-3 7a4 4 0 0 0 6 0l-3-7ZM8 21h8',
  target: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  sparkle: 'M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.4L12 3Z',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
};
const Icon = ({ d, size = 14, style, sw = 2 }: { d: string; size?: number; style?: React.CSSProperties; sw?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw}
    strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, ...style }} aria-hidden="true"><path d={d} /></svg>
);

/* ─── Visual primitives ──────────────────────────────────────────── */
function ScoreRing({ score, c, size = 64 }: { score: number | null; c: P; size?: number }) {
  const stroke = 6;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = score === null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const col = score === null ? c.muted : c.sc(score);
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={c.line} strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} style={{ transition: 'stroke-dashoffset .5s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
        <span style={{ fontSize: size * 0.3, fontWeight: 800, color: score === null ? c.muted : c.ink }}>{score ?? '—'}</span>
        {score !== null && <span style={{ fontSize: size * 0.15, fontWeight: 700, color: col, marginTop: 2 }}>{grade(score)}</span>}
      </div>
    </div>
  );
}

function Bar({ value, max = 100, color, c }: { value: number | null; max?: number; color: string; c: P }) {
  const pct = value === null || max <= 0 ? 0 : Math.min(100, (value / max) * 100);
  return (
    <div style={{ height: 6, borderRadius: 99, background: c.line2, overflow: 'hidden', flex: 1, minWidth: 40 }}>
      <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: color, transition: 'width .4s ease' }} />
    </div>
  );
}

function Sparkline({ points, color, c, width = 120, height = 32 }: { points: number[]; color: string; c: P; width?: number; height?: number }) {
  if (points.length < 2) return <span style={{ fontSize: 12, color: c.muted }}>Not enough scans</span>;
  const min = Math.min(...points, 100) - 5;
  const max = 100;
  const x = (i: number) => (i / (points.length - 1)) * (width - 4) + 2;
  const y = (v: number) => height - 2 - ((v - min) / Math.max(1, max - min)) * (height - 4);
  const d = points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const last = points[points.length - 1];
  return (
    <svg width={width} height={height} aria-label={`Scores over the last ${points.length} scans`} role="img">
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(points.length - 1)} cy={y(last)} r={3} fill={color} />
    </svg>
  );
}

function BestMark({ c }: { c: P }) {
  return (
    <span title="Best among compared vendors" style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: 99,
      background: c.greenBg, color: c.green, flexShrink: 0,
    }}><Icon d={I.check} size={10} sw={3} /></span>
  );
}

/* ─── Chart.js radar ─────────────────────────────────────────────── */
const useChartJS = () => {
  const [ready, setReady] = useState(!!(window as any).Chart);
  useEffect(() => {
    if ((window as any).Chart) { setReady(true); return; }
    const existing = document.querySelector('script[data-chartjs]') as HTMLScriptElement | null;
    const onLoad = () => setReady(true);
    if (existing) { existing.addEventListener('load', onLoad); return () => existing.removeEventListener('load', onLoad); }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
    s.setAttribute('data-chartjs', '1');
    s.onload = onLoad;
    document.head.appendChild(s);
  }, []);
  return ready;
};

function Radar({ labels, series, c }: { labels: string[]; series: { label: string; color: string; values: (number | null)[] }[]; c: P }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const chart = useRef<any>(null);
  const ready = useChartJS();
  useEffect(() => {
    if (!ready || !ref.current || labels.length < 3) return;
    const Chart = (window as any).Chart;
    chart.current?.destroy();
    chart.current = new Chart(ref.current, {
      type: 'radar',
      data: {
        labels,
        datasets: series.map(s => ({
          label: s.label, data: s.values, borderColor: s.color, backgroundColor: `${s.color}10`,
          borderWidth: 2, pointRadius: 2.5, pointBackgroundColor: s.color, spanGaps: true,
        })),
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          r: {
            min: 0, max: 100, ticks: { stepSize: 25, display: false },
            grid: { color: c.isDark ? '#262626' : '#E2E8F0' },
            angleLines: { color: c.isDark ? '#262626' : '#E2E8F0' },
            pointLabels: { font: { size: 10.5, family: sans }, color: c.isDark ? '#94a3b8' : '#64748B' },
          },
        },
      },
    });
    return () => chart.current?.destroy();
  }, [ready, labels, series, c.isDark]);
  return <div style={{ position: 'relative', height: 320 }}><canvas ref={ref} role="img" aria-label="Category scores radar chart" /></div>;
}

/* ─── Vendor search popover ──────────────────────────────────────── */
function VendorSearch({ c, vendors, exclude, onPick, onClose }: {
  c: P; vendors: VendorLite[]; exclude: string[]; onPick: (id: string) => void; onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [onClose]);
  const results = vendors
    .filter(v => !exclude.includes(v.id))
    .filter(v => !q.trim() || `${v.name} ${v.domain}`.toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 8);
  return (
    <div ref={ref} style={{
      position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, minWidth: 260, zIndex: 30,
      background: c.surface, border: `1px solid ${c.line}`, borderRadius: 10,
      boxShadow: c.isDark ? '0 12px 32px rgba(0,0,0,.6)' : '0 12px 32px rgba(15,23,42,.14)', overflow: 'hidden',
    }}>
      <div style={{ position: 'relative', borderBottom: `1px solid ${c.line}` }}>
        <Icon d={I.search} size={14} style={{ position: 'absolute', left: 12, top: 12, color: c.muted }} />
        <input autoFocus aria-label="Search vendors" value={q} onChange={e => setQ(e.target.value)} placeholder="Search vendors…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 34px', border: 'none', outline: 'none', background: 'transparent', color: c.ink, fontSize: 13, fontFamily: sans }} />
      </div>
      <div style={{ maxHeight: 280, overflowY: 'auto', padding: 4 }}>
        {results.length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: c.slate }}>No vendors match.</div>}
        {results.map(v => (
          <button key={v.id} onClick={() => onPick(v.id)}
            onMouseEnter={e => (e.currentTarget.style.background = c.hover)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: 'none', borderRadius: 7, background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: sans }}>
            <VendorLogo name={v.name} domain={v.domain} size={26} isDark={c.isDark} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.name}</span>
              <span style={{ display: 'block', fontSize: 11.5, color: c.slate }}>{v.domain}</span>
            </span>
            {v.score !== null
              ? <span style={{ fontSize: 12.5, fontWeight: 700, color: c.sc(v.score) }}>{v.score}</span>
              : <span style={{ fontSize: 11.5, color: c.muted }}>Not scanned</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─── Page ───────────────────────────────────────────────────────── */
export default function VendorComparison() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { mode } = useCustomTheme();
  const c = palette(mode === 'dark');

  const selected = useMemo(
    () => (params.get('vendors') || '').split(',').map(s => s.trim()).filter(Boolean).slice(0, MAX),
    [params],
  );
  const setSelected = (ids: string[]) => {
    const next = new URLSearchParams(params);
    if (ids.length) next.set('vendors', ids.join(',')); else next.delete('vendors');
    setParams(next, { replace: true });
  };

  const [adding, setAdding] = useState(false);
  const [diffOnly, setDiffOnly] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  const vendorsQuery = useQuery({
    ...vendorQueries.list(),
    select: (list: any[]): VendorLite[] => list.map(v => ({
      id: v.id, name: v.name, domain: v.domain,
      score: typeof v.score === 'number' ? v.score : null,
      riskLevel: v.riskLevel || v.risk_level || null,
      scoreHistory: Array.isArray(v.score_history) ? v.score_history : [],
      previousScore: typeof v.previous_score === 'number' ? v.previous_score : null,
      lastScan: v.lastScan || v.last_scan || null,
      category: v.category || null,
      location: v.location || null,
      lifecycle: v.lifecycle || null,
      dataSensitivity: v.data_sensitivity || null,
      ownerName: v.owner_name || null,
      contractEnd: v.contract_end_date || null,
    })),
  });
  const categoriesQuery = useQuery(vendorQueries.riskCategories());
  const vendors: VendorLite[] = vendorsQuery.data ?? [];
  const categories: string[] = categoriesQuery.data ?? [];

  const postures = useQueries({ queries: selected.map(id => vendorQueries.postureSummary(id)) });

  const cols: Col[] = selected
    .map((id, i) => {
      const vendor = vendors.find(v => v.id === id);
      return vendor ? { vendor, posture: postures[i]?.data as Posture | undefined, loading: !!postures[i]?.isPending, color: COLORS[i] } : null;
    })
    .filter((x): x is Col => !!x);

  const add = (id: string) => { if (!selected.includes(id) && selected.length < MAX) setSelected([...selected, id]); setAdding(false); };
  const remove = (id: string) => setSelected(selected.filter(x => x !== id));

  /* ── derived comparison data ── */
  const catRows = useMemo(() => categories.map(cat => {
    const vals = cols.map(col => {
      const v = col.posture?.category_scores?.[cat];
      // Whole points: the grid shows whole numbers, so "best" must be judged on what the user sees.
      return typeof v === 'number' ? Math.round(v) : null;
    });
    const nums = vals.filter((v): v is number => v !== null);
    const max = nums.length ? Math.max(...nums) : null;
    const min = nums.length ? Math.min(...nums) : null;
    const spread = nums.length >= 2 && max !== null && min !== null ? max - min : 0;
    const winners = max !== null && nums.length >= 2 ? vals.map((v, i) => (v === max ? i : -1)).filter(i => i >= 0) : [];
    return { cat, vals, spread, winner: winners.length === 1 && spread > 0 ? winners[0] : -1 };
  }), [categories, cols]);

  const wins = cols.map((_, i) => catRows.filter(r => r.winner === i).length);
  const ties = catRows.filter(r => r.winner === -1 && r.vals.filter(v => v !== null).length >= 2).length;

  const findingTotal = (col: Col) => (col.posture ? SEVERITIES.reduce((n, s) => n + (col.posture!.counts[s] || 0), 0) : null);

  const ready = cols.length >= MIN;
  const anyLoading = cols.some(col => col.loading);

  // One ordering drives both the rank badges and the verdict: score, then fewest critical+high, then most
  // category wins. Deterministic.
  const ranked = cols.map((col, i) => ({
    i, score: col.vendor.score ?? -1,
    severe: col.posture ? col.posture.counts.critical + col.posture.counts.high : Infinity,
    wins: wins[i],
  })).filter(x => x.score >= 0).sort((a, b) => b.score - a.score || a.severe - b.severe || b.wins - a.wins);
  const rankOf = (i: number) => {
    const pos = ranked.findIndex(r => r.i === i);
    return pos < 0 ? null : pos + 1;
  };

  const verdict = useMemo(() => {
    if (!ready || anyLoading) return null;
    const scored = ranked;
    if (scored.length < 2) return null;
    const top = scored[0];
    const reasons: string[] = [];
    if (top.score > scored[1].score) reasons.push(`highest score (${top.score})`);
    if (scored.every(x => x.i === top.i || x.severe > top.severe)) reasons.push('fewest critical and high findings');
    if (wins[top.i] > 0 && wins.every((w, j) => j === top.i || w < wins[top.i])) reasons.push(`leads ${wins[top.i]} of ${categories.length} categories`);
    const close = scored[0].score - scored[1].score <= 3;
    return { col: cols[top.i], reasons, close };
  }, [ready, anyLoading, cols, wins, categories.length, ranked]);

  const differences = useMemo(() => {
    if (!ready || anyLoading) return [];
    const out: { icon: string; tone: string; text: React.ReactNode }[] = [];
    [...catRows].filter(r => r.spread >= GAP).sort((a, b) => b.spread - a.spread).slice(0, 3).forEach(r => {
      const nums = r.vals.map((v, i) => ({ v, i })).filter(x => x.v !== null) as { v: number; i: number }[];
      const hi = nums.reduce((a, b) => (b.v > a.v ? b : a));
      const lo = nums.reduce((a, b) => (b.v < a.v ? b : a));
      out.push({
        icon: 'scale', tone: c.amber,
        text: <><strong>{r.cat}</strong>: {cols[hi.i].vendor.name} {hi.v} vs {cols[lo.i].vendor.name} {lo.v} <span style={{ color: c.muted }}>({Math.round(r.spread)}-point gap)</span></>,
      });
    });
    const crit = cols.filter(col => (col.posture?.counts.critical || 0) > 0);
    if (crit.length && crit.length < cols.length) {
      out.push({
        icon: 'warn', tone: c.red,
        text: <>{crit.map(col => `${col.vendor.name} (${col.posture!.counts.critical})`).join(', ')} {crit.length === 1 ? 'has' : 'have'} open <strong>critical</strong> findings; the others have none.</>,
      });
    }
    cols.forEach(col => {
      if (col.vendor.score !== null && col.vendor.previousScore !== null) {
        const d = col.vendor.score - col.vendor.previousScore;
        if (Math.abs(d) >= 5) out.push({
          icon: d > 0 ? 'up' : 'down', tone: d > 0 ? c.green : c.red,
          text: <><strong>{col.vendor.name}</strong> {d > 0 ? 'improved' : 'dropped'} {Math.abs(d)} points since its previous scan.</>,
        });
      }
    });
    const stale = cols.filter(col => (daysSince(col.posture?.scan_date || col.vendor.lastScan) ?? 0) > 30);
    if (stale.length) out.push({
      icon: 'clock', tone: c.slate,
      text: <>{stale.map(s => s.vendor.name).join(', ')} {stale.length === 1 ? "hasn't" : "haven't"} been scanned in over 30 days, so {stale.length === 1 ? 'its' : 'their'} scores may be out of date.</>,
    });
    return out;
  }, [ready, anyLoading, catRows, cols, c]);

  /* ── presets ── */
  const scoredVendors = vendors.filter(v => v.score !== null);
  const presets = [
    { label: 'Lowest-scoring vendors', hint: 'Your 3 weakest by score', ids: [...scoredVendors].sort((a, b) => a.score! - b.score!).slice(0, 3).map(v => v.id) },
    { label: 'Best vs. worst', hint: 'Highest and lowest score side by side', ids: scoredVendors.length >= 2 ? [
      [...scoredVendors].sort((a, b) => b.score! - a.score!)[0].id, [...scoredVendors].sort((a, b) => a.score! - b.score!)[0].id,
    ] : [] },
    { label: 'Most recently scanned', hint: 'The freshest data you have', ids: [...vendors].filter(v => v.lastScan).sort((a, b) => new Date(b.lastScan!).getTime() - new Date(a.lastScan!).getTime()).slice(0, 3).map(v => v.id) },
  ].filter(p => new Set(p.ids).size >= 2);

  /* ── actions ── */
  const exportCsv = () => {
    const header = ['', ...cols.map(col => col.vendor.name)];
    const rows: unknown[][] = [
      ['Domain', ...cols.map(col => col.vendor.domain)],
      ['Security score', ...cols.map(col => col.vendor.score ?? '')],
      ['Grade', ...cols.map(col => (col.vendor.score !== null ? grade(col.vendor.score) : ''))],
      ['Risk level', ...cols.map(col => col.vendor.riskLevel ?? '')],
      ['Change since previous scan', ...cols.map(col => (col.vendor.score !== null && col.vendor.previousScore !== null ? col.vendor.score - col.vendor.previousScore : ''))],
      ['Last scanned', ...cols.map(col => fmtDate(col.posture?.scan_date || col.vendor.lastScan))],
      ...SEVERITIES.map(s => [`${s[0].toUpperCase()}${s.slice(1)} findings`, ...cols.map(col => col.posture?.counts[s] ?? '')]),
      ...categories.map(cat => [cat, ...cols.map(col => col.posture?.category_scores?.[cat] ?? '')]),
      ['Category wins', ...wins],
    ];
    const csv = [header, ...rows].map(r => r.map(csvCell).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `vendor-comparison-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const copyLink = () => {
    navigator.clipboard?.writeText(window.location.href).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1800); }).catch(() => {});
  };

  /* ── grid layout ── */
  const slots = Math.min(MAX, cols.length + (cols.length < MAX ? 1 : 0));
  const gridCols = `minmax(150px, 210px) repeat(${slots}, minmax(0, 1fr))`;
  const rowStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: gridCols, borderTop: `1px solid ${c.line2}` };
  const labelCell: React.CSSProperties = { padding: '12px 16px', fontSize: 12.5, fontWeight: 600, color: c.ink2, display: 'flex', alignItems: 'center', gap: 6 };
  const valueCell: React.CSSProperties = { padding: '12px 16px', fontSize: 13, color: c.ink, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, borderLeft: `1px solid ${c.line2}` };
  const emptyCell = <div style={{ ...valueCell }} />;

  const Row = ({ label, hint, cells }: { label: React.ReactNode; hint?: string; cells: React.ReactNode[] }) => (
    <div style={rowStyle}>
      <div style={labelCell} title={hint}>{label}</div>
      {cells.map((cell, i) => <div key={i} style={valueCell}>{cell}</div>)}
      {cols.length < slots && emptyCell}
    </div>
  );

  const Section = ({ id, title, meta, children, right }: { id: string; title: string; meta?: string; children: React.ReactNode; right?: React.ReactNode }) => {
    const isCollapsed = !!collapsed[id];
    return (
      <div style={{ borderTop: `1px solid ${c.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: c.surface2 }}>
          <button onClick={() => setCollapsed(s => ({ ...s, [id]: !isCollapsed }))} aria-expanded={!isCollapsed}
            style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: sans }}>
            <Icon d={I.chevron} size={14} style={{ color: c.muted, transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform .15s' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: c.ink }}>{title}</span>
            {meta && <span style={{ fontSize: 12, color: c.muted }}>{meta}</span>}
          </button>
          <div style={{ marginLeft: 'auto' }}>{right}</div>
        </div>
        {!isCollapsed && children}
      </div>
    );
  };

  const shownCats = diffOnly ? catRows.filter(r => r.spread >= GAP) : catRows;

  return (
    <div style={{ background: c.bg, minHeight: '100%', padding: '4px 4px 40px', fontFamily: sans, color: c.ink }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 22 }}>
        <div>
          <div style={eyebrow(c)}>Reports</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 800, color: c.ink, letterSpacing: '-.02em' }}>Vendor Comparison</h1>
          <div style={{ fontSize: 13.5, color: c.slate, marginTop: 4 }}>
            Put up to {MAX} vendors head to head on security score, findings and every risk category.
          </div>
        </div>
        {ready && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btn(c)} onClick={copyLink}><Icon d={copied ? I.check : I.link} /> {copied ? 'Link copied' : 'Copy link'}</button>
            <button style={btn(c)} onClick={exportCsv}><Icon d={I.download} /> Export CSV</button>
            <button style={btn(c, 'ghost')} onClick={() => setSelected([])}>Clear all</button>
          </div>
        )}
      </div>

      {vendorsQuery.isPending ? (
        <div style={{ ...panel(c), padding: 40, textAlign: 'center', fontSize: 13, color: c.slate }}>Loading vendors…</div>
      ) : vendors.length < MIN ? (
        <div style={{ ...panel(c), padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>Add at least two vendors to compare them</div>
          <div style={{ fontSize: 13, color: c.slate, marginTop: 6 }}>Comparison needs two or more vendors in your portfolio.</div>
          <button style={{ ...btn(c, 'primary'), marginTop: 16 }} onClick={() => navigate('/vendors/add')}><Icon d={I.plus} /> Add vendor</button>
        </div>
      ) : (
        <>
          {/* ── Overview: verdict + radar (only when comparing) ── */}
          {ready && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 1fr)', gap: 16, marginBottom: 16 }}>
              <div style={{ ...panel(c), padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <div style={eyebrow(c)}>Verdict</div>
                  {anyLoading || !verdict ? (
                    <div style={{ fontSize: 13, color: c.slate, marginTop: 10 }}>{anyLoading ? 'Comparing…' : 'Not enough scan data to call a leader yet.'}</div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 12 }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: c.accentBg, color: c.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon d={I.trophy} size={22} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 17, fontWeight: 800, color: c.ink, letterSpacing: '-.01em' }}>
                          {verdict.col.vendor.name} {verdict.close ? 'edges ahead' : 'has the strongest posture'}
                        </div>
                        <div style={{ fontSize: 12.5, color: c.slate, marginTop: 3 }}>
                          {verdict.reasons.length
                            ? verdict.reasons.join(' · ').replace(/^./, ch => ch.toUpperCase())
                            : 'Leads on overall score'}
                          {verdict.close && ' — the gap is within 3 points, so treat them as close.'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={eyebrow(c)}>Category wins</span>
                    <span style={{ fontSize: 11.5, color: c.muted }}>{ties ? `${ties} tied` : ''}</span>
                  </div>
                  <div style={{ display: 'flex', height: 10, borderRadius: 99, overflow: 'hidden', background: c.line2, marginTop: 10 }}>
                    {cols.map((col, i) => wins[i] > 0 && (
                      <div key={col.vendor.id} title={`${col.vendor.name}: ${wins[i]}`} style={{ width: `${(wins[i] / Math.max(1, categories.length)) * 100}%`, background: col.color }} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 10 }}>
                    {cols.map((col, i) => (
                      <span key={col.vendor.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: c.ink2 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 99, background: col.color }} />
                        {col.vendor.name} <strong style={{ color: c.ink }}>{wins[i]}</strong>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div style={eyebrow(c)}>What stands out</div>
                  {differences.length === 0 ? (
                    <div style={{ fontSize: 12.5, color: c.slate, marginTop: 10 }}>
                      {anyLoading ? 'Comparing…' : `No large gaps — these vendors are within ${GAP} points on every category.`}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                      {differences.map((d, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: c.ink2, lineHeight: 1.5 }}>
                          <Icon d={I[d.icon]} size={14} style={{ color: d.tone, marginTop: 2 }} />
                          <span>{d.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ ...panel(c), padding: 22 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <span style={eyebrow(c)}>Risk profile</span>
                  <span style={{ fontSize: 11.5, color: c.muted }}>Category scores, 0–100</span>
                </div>
                {anyLoading || categoriesQuery.isPending ? (
                  <div style={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: c.slate }}>Loading…</div>
                ) : (
                  <Radar c={c} labels={categories} series={cols.map((col, i) => ({ label: col.vendor.name, color: col.color, values: catRows.map(r => r.vals[i]) }))} />
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14, marginTop: 8 }}>
                  {cols.map(col => (
                    <span key={col.vendor.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: c.ink2 }}>
                      <span style={{ width: 10, height: 3, borderRadius: 2, background: col.color }} />{col.vendor.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Head-to-head grid ── */}
          <div style={{ ...panel(c), overflow: 'visible' }}>
            <div>
              <div>
                {/* sticky vendor header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: gridCols, position: 'sticky', top: 64, zIndex: 10,
                  background: c.surface, borderBottom: `1px solid ${c.line}`, borderRadius: '12px 12px 0 0',
                }}>
                  <div style={{ padding: 16, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                    <div style={eyebrow(c)}>Comparing</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: c.ink, marginTop: 2 }}>{cols.length}<span style={{ fontSize: 13, fontWeight: 600, color: c.muted }}> / {MAX} vendors</span></div>
                  </div>
                  {cols.map((col, i) => {
                    const rank = rankOf(i);
                    return (
                      <div key={col.vendor.id} style={{ padding: 16, borderLeft: `1px solid ${c.line2}`, position: 'relative' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          <VendorLogo name={col.vendor.name} domain={col.vendor.domain} size={30} isDark={c.isDark} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 99, background: col.color, flexShrink: 0 }} />
                              <span style={{ fontSize: 13.5, fontWeight: 700, color: c.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.vendor.name}</span>
                            </div>
                            <div style={{ fontSize: 11.5, color: c.slate, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.vendor.domain}</div>
                          </div>
                          <button aria-label={`Remove ${col.vendor.name}`} title="Remove" onClick={() => remove(col.vendor.id)} style={{ ...btn(c, 'ghost', true), padding: 4 }}>
                            <Icon d={I.close} size={13} />
                          </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
                          <ScoreRing score={col.vendor.score} c={c} size={58} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {rank !== null && ready && !anyLoading && (
                              <span style={{
                                alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                                background: rank === 1 ? c.greenBg : c.surface2, color: rank === 1 ? c.green : c.slate,
                              }}>{rank === 1 ? '#1 Leader' : `#${rank}`}</span>
                            )}
                            <button onClick={() => navigate(`/vendors/${col.vendor.id}`)} style={{ ...btn(c, 'ghost', true), padding: 0, color: c.accent, justifyContent: 'flex-start' }}>
                              View vendor <Icon d={I.external} size={11} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {cols.length < MAX && (
                    <div style={{ padding: 16, borderLeft: `1px solid ${c.line2}`, position: 'relative' }}>
                      <button onClick={() => setAdding(a => !a)} style={{
                        width: '100%', height: '100%', minHeight: 116, borderRadius: 10, border: `1.5px dashed ${adding ? c.accent : c.line}`,
                        background: 'transparent', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                        color: adding ? c.accent : c.slate, fontFamily: sans,
                      }}>
                        <span style={{ width: 30, height: 30, borderRadius: 99, background: c.accentBg, color: c.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon d={I.plus} size={16} />
                        </span>
                        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{cols.length === 0 ? 'Add a vendor' : 'Add vendor'}</span>
                        <span style={{ fontSize: 11, color: c.muted }}>{MAX - cols.length} slot{MAX - cols.length === 1 ? '' : 's'} left</span>
                      </button>
                      {adding && <VendorSearch c={c} vendors={vendors} exclude={selected} onPick={add} onClose={() => setAdding(false)} />}
                    </div>
                  )}
                </div>

                {!ready ? (
                  <div style={{ padding: '36px 24px 40px', textAlign: 'center' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 14, margin: '0 auto', background: c.accentBg, color: c.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon d={I.scale} size={22} />
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: c.ink, marginTop: 14 }}>
                      {cols.length === 0 ? 'Pick two or more vendors to compare' : 'Add one more vendor to start comparing'}
                    </div>
                    <div style={{ fontSize: 13, color: c.slate, marginTop: 6 }}>Use the “Add vendor” slot above, or start from a suggestion.</div>
                    {presets.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
                        {presets.map(p => (
                          <button key={p.label} onClick={() => setSelected(Array.from(new Set(p.ids)).slice(0, MAX))}
                            onMouseEnter={e => (e.currentTarget.style.borderColor = c.accent)} onMouseLeave={e => (e.currentTarget.style.borderColor = c.line)}
                            style={{ textAlign: 'left', padding: '12px 14px', borderRadius: 10, border: `1px solid ${c.line}`, background: c.surface, cursor: 'pointer', fontFamily: sans, minWidth: 210 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: c.ink }}>
                              <Icon d={I.sparkle} size={13} style={{ color: c.accent }} />{p.label}
                            </div>
                            <div style={{ fontSize: 11.5, color: c.slate, marginTop: 3 }}>{p.hint}</div>
                            <div style={{ display: 'flex', marginTop: 8 }}>
                              {Array.from(new Set(p.ids)).map((id, k) => {
                                const v = vendors.find(x => x.id === id)!;
                                return <span key={id} style={{ marginLeft: k ? -6 : 0, borderRadius: 8, border: `2px solid ${c.surface}` }}><VendorLogo name={v.name} domain={v.domain} size={22} isDark={c.isDark} /></span>;
                              })}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <Section id="overview" title="Overview">
                      <Row label="Security score" cells={cols.map(col => {
                        const best = Math.max(...cols.map(x => x.vendor.score ?? -1));
                        const s = col.vendor.score;
                        return s === null ? <span style={{ color: c.muted }}>Not scanned</span> : (<>
                          <strong style={{ fontSize: 15, color: c.sc(s), minWidth: 28 }}>{s}</strong>
                          <Bar value={s} color={c.sc(s)} c={c} />
                          {s === best && cols.filter(x => x.vendor.score === best).length === 1 && <BestMark c={c} />}
                        </>);
                      })} />
                      <Row label="Risk level" cells={cols.map(col => {
                        const l = (col.vendor.riskLevel || '').toLowerCase();
                        const col2 = l === 'critical' || l === 'high' ? c.red : l === 'medium' ? c.amber : l === 'low' ? c.green : c.muted;
                        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                          <span style={{ width: 7, height: 7, borderRadius: 99, background: col2 }} />{col.vendor.riskLevel || 'Unknown'}
                        </span>;
                      })} />
                      <Row label="Since previous scan" cells={cols.map(col => {
                        if (col.vendor.score === null || col.vendor.previousScore === null) return <span style={{ color: c.muted }}>No earlier scan</span>;
                        const d = col.vendor.score - col.vendor.previousScore;
                        if (d === 0) return <span style={{ color: c.slate }}>No change</span>;
                        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700, color: d > 0 ? c.green : c.red }}>
                          <Icon d={d > 0 ? I.up : I.down} size={12} /> {d > 0 ? '+' : ''}{d} points
                        </span>;
                      })} />
                      <Row label="Score trend" hint="Last 8 scans, oldest to newest" cells={cols.map(col => <Sparkline points={col.vendor.scoreHistory} color={col.color} c={c} />)} />
                      <Row label="Last scanned" cells={cols.map(col => {
                        const iso = col.posture?.scan_date || col.vendor.lastScan;
                        const d = daysSince(iso);
                        return <span style={{ color: d !== null && d > 30 ? c.amber : c.ink2 }} title={fmtDate(iso)}>{agoText(iso)}</span>;
                      })} />
                    </Section>

                    <Section id="findings" title="Open findings" meta="Lower is better" >
                      <Row label="Total" cells={cols.map(col => {
                        const n = findingTotal(col);
                        const all = cols.map(findingTotal).filter((x): x is number => x !== null);
                        const best = all.length ? Math.min(...all) : null;
                        return col.loading ? <span style={{ color: c.muted }}>…</span> : n === null ? <span style={{ color: c.muted }}>—</span> : <>
                          <strong style={{ fontSize: 14 }}>{n}</strong>
                          {n === best && all.filter(x => x === best).length === 1 && <BestMark c={c} />}
                        </>;
                      })} />
                      {SEVERITIES.map(sev => {
                        const tone = sev === 'critical' ? c.red : sev === 'high' ? c.orange : sev === 'medium' ? c.amber : c.slate;
                        const all = cols.map(col => col.posture?.counts[sev] ?? 0);
                        const max = Math.max(1, ...all);
                        return (
                          <Row key={sev} label={<><span style={{ width: 7, height: 7, borderRadius: 99, background: tone }} />{sev[0].toUpperCase() + sev.slice(1)}</>}
                            cells={cols.map(col => {
                              const n = col.posture?.counts[sev];
                              return col.loading ? <span style={{ color: c.muted }}>…</span> : <>
                                <span style={{ fontWeight: 700, minWidth: 22, color: n ? tone : c.muted }}>{n ?? 0}</span>
                                <Bar value={n ?? 0} max={max} color={tone} c={c} />
                              </>;
                            })} />
                        );
                      })}
                    </Section>

                    <Section id="categories" title="Risk categories" meta={`${shownCats.length} of ${catRows.length}`} right={
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: c.slate, cursor: 'pointer', userSelect: 'none' }}>
                        <input type="checkbox" checked={diffOnly} onChange={e => setDiffOnly(e.target.checked)} />
                        Only gaps of {GAP}+ points
                      </label>
                    }>
                      {anyLoading ? (
                        <div style={{ padding: 20, fontSize: 13, color: c.slate }}>Loading category scores…</div>
                      ) : shownCats.length === 0 ? (
                        <div style={{ padding: '22px 16px', fontSize: 13, color: c.slate, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Icon d={I.check} size={14} style={{ color: c.green }} /> No category differs by {GAP} points or more.
                        </div>
                      ) : shownCats.map(r => (
                        <Row key={r.cat} label={<>
                          <span style={{ flex: 1 }}>{r.cat}</span>
                          {r.spread >= GAP && <span title={`${Math.round(r.spread)}-point gap`} style={{ fontSize: 10.5, fontWeight: 700, color: c.amber }}>Δ{Math.round(r.spread)}</span>}
                        </>} cells={r.vals.map((v, i) => v === null ? <span style={{ color: c.muted }}>No data</span> : <>
                          <span style={{ fontWeight: 700, minWidth: 30, color: c.sc(v) }}>{Math.round(v)}</span>
                          <Bar value={v} color={c.sc(v)} c={c} />
                          {r.winner === i && <BestMark c={c} />}
                        </>)} />
                      ))}
                    </Section>

                    <Section id="top" title="Top findings">
                      <div style={{ display: 'grid', gridTemplateColumns: gridCols, borderTop: `1px solid ${c.line2}` }}>
                        <div style={{ ...labelCell, alignItems: 'flex-start', color: c.slate, fontWeight: 500, fontSize: 12 }}>The most severe open findings from each vendor's latest scan.</div>
                        {cols.map(col => (
                          <div key={col.vendor.id} style={{ padding: '12px 16px', borderLeft: `1px solid ${c.line2}`, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                            {col.loading ? <span style={{ fontSize: 12.5, color: c.muted }}>Loading…</span>
                              : !col.posture?.has_scan ? <span style={{ fontSize: 12.5, color: c.muted }}>Not scanned yet</span>
                              : col.posture.top_findings.length === 0 ? <span style={{ fontSize: 12.5, color: c.green, display: 'flex', alignItems: 'center', gap: 5 }}><Icon d={I.check} size={12} /> No open findings</span>
                              : col.posture.top_findings.map((f, k) => {
                                const s = (f.severity || '').toLowerCase();
                                const tone = s === 'critical' ? c.red : s === 'high' ? c.orange : s === 'medium' ? c.amber : c.slate;
                                return (
                                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                    <span style={{ width: 6, height: 6, borderRadius: 99, background: tone, flexShrink: 0 }} />
                                    <span title={f.name} style={{ fontSize: 12.5, color: c.ink2, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                                    <span style={{ fontSize: 10.5, fontWeight: 700, color: tone }}>{f.severity}</span>
                                  </div>
                                );
                              })}
                            <button onClick={() => navigate(`/vendors/${col.vendor.id}`, { state: { tab: 'issues' } })}
                              style={{ ...btn(c, 'ghost', true), padding: 0, marginTop: 4, color: c.accent, justifyContent: 'flex-start' }}>
                              All issues <Icon d={I.external} size={11} />
                            </button>
                          </div>
                        ))}
                        {cols.length < slots && emptyCell}
                      </div>
                    </Section>

                    <Section id="context" title="Business context">
                      <Row label="Category" cells={cols.map(col => <span style={{ color: col.vendor.category ? c.ink2 : c.muted }}>{col.vendor.category || 'Not set'}</span>)} />
                      <Row label="Data handled" cells={cols.map(col => <span style={{ color: col.vendor.dataSensitivity ? c.ink2 : c.muted }}>{col.vendor.dataSensitivity ? (SENSITIVITY[col.vendor.dataSensitivity] || col.vendor.dataSensitivity) : 'Not classified'}</span>)} />
                      <Row label="Lifecycle" cells={cols.map(col => <span style={{ color: c.ink2 }}>{col.vendor.lifecycle ? (LIFECYCLE[col.vendor.lifecycle] || col.vendor.lifecycle) : '—'}</span>)} />
                      <Row label="Owner" cells={cols.map(col => <span style={{ color: col.vendor.ownerName ? c.ink2 : c.muted }}>{col.vendor.ownerName || 'Unassigned'}</span>)} />
                      <Row label="Location" cells={cols.map(col => <span style={{ color: col.vendor.location ? c.ink2 : c.muted }}>{col.vendor.location || '—'}</span>)} />
                      <Row label="Contract ends" cells={cols.map(col => {
                        const iso = col.vendor.contractEnd;
                        const d = iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000) : null;
                        return <span style={{ color: d === null ? c.muted : d < 0 ? c.red : d <= 90 ? c.amber : c.ink2 }}>
                          {iso ? `${fmtDate(iso)}${d !== null && d < 0 ? ' (expired)' : d !== null && d <= 90 ? ` (${d} days)` : ''}` : '—'}
                        </span>;
                      })} />
                    </Section>
                  </>
                )}
              </div>
            </div>
          </div>
          {ready && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12, fontSize: 11.5, color: c.muted }}>
              <Icon d={I.info} size={12} /> Scores come from each vendor's latest completed scan. The verdict and highlights are calculated from those numbers.
            </div>
          )}
        </>
      )}
    </div>
  );
}
